import { useCallback } from "react";

export function isAnnotationStudioTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const editingTarget = target.closest<HTMLElement>(
    "input, textarea, select, [contenteditable='true']",
  );
  if (editingTarget === null) return false;
  return !(editingTarget instanceof HTMLInputElement && editingTarget.type === "range");
}

export function useAnnotationStudioShortcuts({
  blocked,
  authoringBlocked,
  authoring,
  canUndo,
  canRedo,
  saveAvailable,
  annotationOpenAvailable,
  onUndo,
  onRedo,
  onSave,
  onAnnotationOpen,
}: {
  blocked: boolean;
  authoringBlocked: boolean;
  authoring: boolean;
  canUndo: boolean;
  canRedo: boolean;
  saveAvailable: boolean;
  annotationOpenAvailable: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  onAnnotationOpen: () => void;
}) {
  return useCallback(
    (event: KeyboardEvent): boolean => {
      if (
        event.defaultPrevented ||
        blocked ||
        isAnnotationStudioTextEditingTarget(event.target) ||
        event.altKey ||
        event.isComposing ||
        event.keyCode === 229
      ) {
        return false;
      }

      const key = event.key.toLowerCase();
      const annotationOpenRequested =
        event.code === "KeyC" &&
        event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        (!authoring || !authoringBlocked) &&
        annotationOpenAvailable;
      if (annotationOpenRequested) {
        event.preventDefault();
        event.stopPropagation();
        if (!event.repeat) onAnnotationOpen();
        return true;
      }

      if (event.ctrlKey || event.metaKey) {
        if (key === "s" && authoring && !authoringBlocked) {
          event.preventDefault();
          event.stopPropagation();
          if (!event.repeat && saveAvailable) onSave();
          return true;
        }
        const redoRequested = key === "y" || (key === "z" && event.shiftKey);
        const undoRequested = key === "z" && !event.shiftKey;
        if (undoRequested && canUndo && !authoringBlocked) {
          event.preventDefault();
          onUndo();
          return true;
        } else if (redoRequested && canRedo && !authoringBlocked) {
          event.preventDefault();
          onRedo();
          return true;
        }
        return false;
      }
      return false;
    },
    [
      annotationOpenAvailable,
      authoring,
      authoringBlocked,
      blocked,
      canRedo,
      canUndo,
      onAnnotationOpen,
      onRedo,
      onSave,
      onUndo,
      saveAvailable,
    ],
  );
}
